// ═════════════════════════════════════════════════════════════════════════════
// Auto tab — NHCX payment notice (payer-initiated)
//
// No submit form. The payer posts the notice; this screen lists cached entries
// (GET /responses, use_case=payment_notice) and acknowledges with one field.
// Contract: PAYMENT_NOTICE_UI_INTEGRATION.md (repo root).
// ═════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { C, fmtINR } from "../constants";
import IpdTextField from "../../ipd/utils/IpdTextField";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import {
  listPaymentNotices,
  acknowledgePaymentNotice,
  initDummyPaymentNotice,
  pollNhcxResponse,
  normalizeNoticeError,
} from "../payment-notice-api";

const SANDBOX_PROVIDER_ID = "1000004481@hcx";
const INBOX_POLL_MS = 2000;
const INBOX_MAX_POLLS = 8;

const SAMPLE_NOTICE = {
  correlation_id: "preview-payment-notice",
  use_case: "payment_notice",
  status: "received",
  acked: false,
  sender_code: "1000003538@hcx",
  workflow_id: "30",
  parsed: {
    notice: {
      identifier: "643940292",
      amount: 16671,
      currency: "INR",
      payment_date: null,
      payment_status: "paid",
      created: "2026-08-26T13:22:05+05:30",
      status: "active",
    },
    reconciliation: {
      payment_identifier: "UTR123456789",
      payment_amount: 16671,
      payment_amount_currency: "INR",
      period: { start: "2026-08-01", end: "2026-08-26" },
      outcome: "complete",
      disposition: "Settled against claim",
      payment_date: "2026-08-26",
      details: [
        { claim_ref: "CLM-2026-0042", type_code: "claim", date: "2026-08-26", amount: 16671 },
      ],
    },
  },
  received_at: "2026-08-26T07:52:05Z",
};

const cardStyle = {
  background: C.card,
  borderRadius: "12px",
  border: `1px solid ${C.border}`,
  boxShadow: "0 2px 8px rgba(13,35,63,0.07)",
};
const innerCardStyle = {
  border: `1px solid ${C.border}`,
  borderRadius: "10px",
  padding: "16px",
  background: "#fff",
  display: "flex",
  flexDirection: "column",
  gap: "14px",
};
const labelStyle = {
  fontSize: "11px",
  fontWeight: "700",
  color: C.textSub,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  display: "block",
  lineHeight: 1.4,
  marginBottom: "6px",
};

function Pill({ tone = "grey", children }) {
  const tones = {
    grey: { bg: "#f1f3f5", fg: C.textSub, br: C.border },
    green: { bg: "#e9f7ef", fg: C.success, br: "#b7e4c7" },
    red: { bg: "#fdecec", fg: C.danger, br: "#f5c2c0" },
    blue: { bg: C.accent, fg: C.primary, br: "#bfe3fb" },
    amber: { bg: "#fff8e1", fg: C.warning, br: "#ffe69c" },
  };
  const t = tones[tone] || tones.grey;
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: "700",
        padding: "3px 9px",
        borderRadius: "20px",
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.br}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function PreviewBanner() {
  return (
    <div
      style={{
        fontSize: "12px",
        fontWeight: "600",
        color: "#B45309",
        background: "#FFF8E1",
        border: "1px solid #FFE082",
        borderRadius: "8px",
        padding: "8px 12px",
      }}
    >
      Preview — sample data so you can see this screen. This is not a payer settlement.
    </div>
  );
}

function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function noticeHeadline(item) {
  const parsed = item?.parsed && !item.parsed.error ? item.parsed : {};
  return parsed.notice || {};
}

function noticeRecon(item) {
  const parsed = item?.parsed && !item.parsed.error ? item.parsed : {};
  return parsed.reconciliation || null;
}

function isAcked(item) {
  return Boolean(item?.acked) || String(item?.status || "") === "acked";
}

function sortNotices(list) {
  return [...list].sort((a, b) => {
    const aOpen = isAcked(a) ? 1 : 0;
    const bOpen = isAcked(b) ? 1 : 0;
    if (aOpen !== bOpen) return aOpen - bOpen;
    return String(b.received_at || "").localeCompare(String(a.received_at || ""));
  });
}

function amountsDiffer(notice, recon) {
  if (!recon || recon.payment_amount == null || notice.amount == null) return false;
  return Number(notice.amount) !== Number(recon.payment_amount);
}

export default function PaymentNoticePanel() {
  const [notices, setNotices] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [description, setDescription] = useState("");
  const [acking, setAcking] = useState(false);
  const [ackMessage, setAckMessage] = useState("");
  const [preview, setPreview] = useState(false);
  const [providerId, setProviderId] = useState(SANDBOX_PROVIDER_ID);
  const [claimNumber, setClaimNumber] = useState("");
  const [initing, setIniting] = useState(false);
  const [initMsg, setInitMsg] = useState("");

  const selected = notices.find((n) => n.correlation_id === selectedId) || null;
  const pendingCount = notices.filter((n) => !isAcked(n)).length;

  const loadInbox = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const list = sortNotices(await listPaymentNotices());
      setNotices((prev) => {
        const previewRows = prev.filter((n) => n.correlation_id === SAMPLE_NOTICE.correlation_id);
        const merged = [...previewRows, ...list.filter((n) => n.correlation_id !== SAMPLE_NOTICE.correlation_id)];
        return sortNotices(merged);
      });
      return list;
    } catch (err) {
      setError(normalizeNoticeError(err, "list"));
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (selectedId) return;
    const firstOpen = notices.find((n) => !isAcked(n));
    const first = firstOpen || notices[0];
    if (first?.correlation_id) setSelectedId(first.correlation_id);
  }, [notices, selectedId]);

  const handleSkip = () => {
    setPreview(true);
    setError(null);
    setAckMessage("");
    setNotices((prev) => {
      if (prev.some((n) => n.correlation_id === SAMPLE_NOTICE.correlation_id)) return prev;
      return sortNotices([SAMPLE_NOTICE, ...prev]);
    });
    setSelectedId(SAMPLE_NOTICE.correlation_id);
  };

  const handleRefresh = () => {
    setAckMessage("");
    loadInbox();
  };

  const handleAcknowledge = async () => {
    if (!selected?.correlation_id || isAcked(selected) || acking) return;
    if (preview && selected.correlation_id === SAMPLE_NOTICE.correlation_id) {
      const now = new Date().toISOString();
      setNotices((prev) =>
        prev.map((n) =>
          n.correlation_id === selected.correlation_id
            ? { ...n, status: "acked", acked: true, acked_at: now }
            : n
        )
      );
      setAckMessage("Acknowledged (preview).");
      return;
    }
    setAcking(true);
    setError(null);
    setAckMessage("");
    try {
      await acknowledgePaymentNotice({
        correlationId: selected.correlation_id,
        description,
      });
      const confirmed = await pollNhcxResponse(selected.correlation_id);
      const next =
        confirmed?.pending
          ? { ...selected, status: "acked", acked: true, acked_at: new Date().toISOString() }
          : { ...selected, ...confirmed, correlation_id: selected.correlation_id };
      setNotices((prev) =>
        prev.map((n) => (n.correlation_id === selected.correlation_id ? next : n))
      );
      setAckMessage("Acknowledgement sent to the payer. Nothing further arrives — this exchange is closed.");
    } catch (err) {
      setError(normalizeNoticeError(err));
    } finally {
      setAcking(false);
    }
  };

  const handleDummyInit = async () => {
    if (!providerId.trim()) {
      setInitMsg("Provider NHCX participant code is required.");
      return;
    }
    setIniting(true);
    setInitMsg("");
    setError(null);
    try {
      const res = await initDummyPaymentNotice({
        providerId: providerId.trim(),
        claimNumber: claimNumber.trim(),
      });
      setInitMsg("Dummy payer triggered — watching the inbox for the notice.");
      const seededId = res?.correlation_id || "";
      let found = false;
      for (let n = 0; n < INBOX_MAX_POLLS; n += 1) {
        await new Promise((r) => setTimeout(r, INBOX_POLL_MS));
        const list = await loadInbox({ silent: true });
        const match = seededId
          ? list.find((item) => item.correlation_id === seededId)
          : list.find((item) => !isAcked(item));
        if (match) {
          setSelectedId(match.correlation_id);
          found = true;
          break;
        }
      }
      setInitMsg(
        found
          ? "Payment notice received."
          : "Still waiting — refresh the inbox in a moment if it has not appeared."
      );
    } catch (err) {
      setInitMsg(normalizeNoticeError(err).message);
    } finally {
      setIniting(false);
    }
  };

  const notice = selected ? noticeHeadline(selected) : {};
  const recon = selected ? noticeRecon(selected) : null;
  const payDate = notice.payment_date || notice.created || selected?.received_at || "";
  const parseFailed = Boolean(selected?.parsed?.error);
  const acked = selected ? isAcked(selected) : false;

  return (
    <div style={cardStyle}>
      <div
        style={{
          padding: "16px 20px",
          borderBottom: `1px solid ${C.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <SvgIcon name="wallet" size={16} color={C.primary} />
          <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: C.text }}>
            NHCX Payment Notice · Payer settlement
          </h3>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <Pill tone={pendingCount ? "green" : "grey"}>
            {pendingCount ? `${pendingCount} to acknowledge` : "Inbox clear"}
          </Pill>
        </div>
      </div>

      <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "16px" }}>
        {preview && <PreviewBanner />}

        <div style={{ fontSize: "13px", color: C.textSub }}>
          After a claim settles the payer sends a notice unprompted. Confirm receipt here — it is a
          statement that this organisation has seen the payment, not a dispute.
        </div>

        <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <IpdButton
            variant="outlined"
            onClick={handleRefresh}
            disabled={loading}
            loading={loading}
            startIcon={<SvgIcon name="refresh" size={14} color={C.primary} />}
          >
            Refresh inbox
          </IpdButton>
          <IpdButton variant="outlined" onClick={handleSkip}>
            Skip — preview this screen
          </IpdButton>
        </div>

        {error && (
          <div style={{ border: "1px solid #f5c2c0", background: "#fdecec", borderRadius: "10px", padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <SvgIcon name="alert-triangle" size={15} color={C.danger} />
              <span style={{ fontSize: "13px", fontWeight: "700", color: C.danger }}>{error.message}</span>
            </div>
            {error.status === 404 && (
              <div style={{ fontSize: "12px", color: C.text, marginTop: "6px" }}>
                Retry will not reconstruct a lost cache. Ask the payer to re-send the notice.
              </div>
            )}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "16px",
            alignItems: "start",
          }}
        >
          <div style={innerCardStyle}>
            <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Inbox
            </span>
            {loading && notices.length === 0 ? (
              <div style={{ fontSize: "13px", color: C.textSub }}>Loading notices…</div>
            ) : notices.length === 0 ? (
              <div style={{ fontSize: "13px", color: C.textSub }}>
                No payment notices cached. They arrive after a claim settles, or trigger one from the
                sandbox panel.
              </div>
            ) : (
              notices.map((item) => {
                const n = noticeHeadline(item);
                const r = noticeRecon(item);
                const done = isAcked(item);
                const active = item.correlation_id === selectedId;
                return (
                  <button
                    key={item.correlation_id}
                    type="button"
                    onClick={() => {
                      setSelectedId(item.correlation_id);
                      setAckMessage("");
                      setError(null);
                    }}
                    style={{
                      textAlign: "left",
                      cursor: "pointer",
                      border: `1px solid ${active ? C.primary : done ? C.border : "#A5D6A7"}`,
                      background: active ? C.accent : done ? "#fff" : "#E6F4EA",
                      borderRadius: "10px",
                      padding: "12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", gap: "8px", alignItems: "center" }}>
                      <span style={{ fontSize: "16px", fontWeight: "800", color: C.text }}>
                        {fmtINR(n.amount)}
                      </span>
                      <Pill tone={done ? "grey" : "green"}>{done ? "Acknowledged" : n.payment_status || "paid"}</Pill>
                    </div>
                    <div style={{ fontSize: "12px", color: C.textSub, fontFamily: "monospace" }}>
                      {r?.payment_identifier || "UTR pending"}
                    </div>
                    <div style={{ fontSize: "11px", color: C.textSub }}>
                      {formatWhen(item.received_at || n.created) || "—"}
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {!selected ? (
            <div style={{ ...innerCardStyle, alignItems: "center", textAlign: "center", padding: "40px 20px" }}>
              <SvgIcon name="wallet" size={26} color={C.primary} />
              <div style={{ fontSize: "15px", fontWeight: "700", color: C.text }}>No notice selected</div>
              <div style={{ fontSize: "13px", color: C.textSub }}>
                Pick a notice from the inbox, or skip to preview the acknowledgement screen.
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {parseFailed && (
                <div style={{ ...innerCardStyle, background: "#fdecec", borderColor: "#f5c2c0" }}>
                  <div style={{ fontSize: "13px", color: C.danger, fontWeight: "700" }}>
                    The notice could not be parsed
                  </div>
                  <div style={{ fontSize: "12px", color: C.text }}>{selected.parsed.error}</div>
                </div>
              )}

              <div style={innerCardStyle}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                  <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Payment
                  </span>
                  <Pill tone={acked ? "grey" : "green"}>
                    {acked ? "Acknowledged" : notice.payment_status || selected.status || "received"}
                  </Pill>
                </div>
                <div style={{ fontSize: "28px", fontWeight: "800", color: C.success }}>
                  {fmtINR(notice.amount)}
                  {notice.currency && notice.currency !== "INR" ? (
                    <span style={{ fontSize: "14px", marginLeft: "8px", color: C.textSub }}>{notice.currency}</span>
                  ) : null}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", gap: "10px" }}>
                  <span style={{ color: C.textSub }}>UTR / bank reference</span>
                  <span style={{ fontWeight: "800", color: C.text, fontFamily: "monospace" }}>
                    {recon?.payment_identifier || "—"}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                  <span style={{ color: C.textSub }}>Payment date</span>
                  <span style={{ fontWeight: "700", color: C.text }}>{formatWhen(payDate) || "—"}</span>
                </div>
                {notice.identifier && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                    <span style={{ color: C.textSub }}>Notice ref</span>
                    <span style={{ fontFamily: "monospace", color: C.text }}>{notice.identifier}</span>
                  </div>
                )}
                {acked && (selected.acked_at || selected.received_at) && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
                    <span style={{ color: C.textSub }}>Acknowledged at</span>
                    <span style={{ fontWeight: "700", color: C.text }}>
                      {formatWhen(selected.acked_at) || "—"}
                    </span>
                  </div>
                )}
              </div>

              {recon && (
                <div style={innerCardStyle}>
                  <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Reconciliation
                  </span>
                  {amountsDiffer(notice, recon) && (
                    <div style={{ fontSize: "12px", color: C.warning, fontWeight: "600" }}>
                      Notice amount {fmtINR(notice.amount)} differs from settled {fmtINR(recon.payment_amount)} — both are shown.
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                    <span style={{ color: C.textSub }}>Settled amount</span>
                    <span style={{ fontWeight: "700", color: C.text }}>
                      {fmtINR(recon.payment_amount)}
                      {recon.payment_amount_currency && recon.payment_amount_currency !== "INR"
                        ? ` ${recon.payment_amount_currency}`
                        : ""}
                    </span>
                  </div>
                  {recon.outcome && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                      <span style={{ color: C.textSub }}>Outcome</span>
                      <span style={{ fontWeight: "700", color: C.text }}>{recon.outcome}</span>
                    </div>
                  )}
                  {recon.disposition && (
                    <div style={{ fontSize: "12px", color: C.textSub }}>{recon.disposition}</div>
                  )}
                  {(recon.period?.start || recon.period?.end) && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                      <span style={{ color: C.textSub }}>Period</span>
                      <span style={{ fontWeight: "700", color: C.text }}>
                        {recon.period.start || "—"} → {recon.period.end || "—"}
                      </span>
                    </div>
                  )}
                  {(recon.details || []).length > 0 && (
                    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
                      <div style={{ fontSize: "11px", fontWeight: "700", color: C.textSub, marginBottom: "8px", textTransform: "uppercase" }}>
                        Claims in this notice
                      </div>
                      {(recon.details || []).map((row, i) => (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: "10px",
                            fontSize: "12px",
                            padding: "6px 0",
                            borderTop: i ? `1px solid ${C.border}` : "none",
                          }}
                        >
                          <span style={{ color: C.text }}>
                            {row.claim_ref || `Claim ${i + 1}`}
                            {row.date ? <span style={{ color: C.textSub }}> · {row.date}</span> : null}
                          </span>
                          <span style={{ fontWeight: "700", color: C.text }}>{fmtINR(row.amount)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={innerCardStyle}>
                <span style={{ fontSize: "12px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Acknowledge
                </span>
                <div style={{ fontSize: "12px", color: C.textSub }}>
                  There is no reject on this endpoint. If the amount is wrong, settle that outside NHCX.
                  Acknowledging twice resends; it does not fail.
                </div>
                {!acked && (
                  <>
                    <div>
                      <label style={labelStyle}>Note to payer (optional)</label>
                      <IpdTextField
                        fullWidth
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Payment received and reconciled"
                      />
                    </div>
                    <IpdButton
                      onClick={handleAcknowledge}
                      disabled={acking}
                      loading={acking}
                      startIcon={<SvgIcon name="check" size={14} color="#fff" />}
                    >
                      {acking ? "Acknowledging…" : "Acknowledge payment"}
                    </IpdButton>
                  </>
                )}
                {acked && (
                  <div style={{ fontSize: "13px", fontWeight: "600", color: C.success }}>
                    Acknowledged. Kept here as the audit trail — nothing further arrives from the payer.
                  </div>
                )}
                {ackMessage && <div style={{ fontSize: "12px", color: C.text }}>{ackMessage}</div>}
                <div style={{ fontSize: "10px", color: C.textSub, fontFamily: "monospace", wordBreak: "break-all", borderTop: `1px solid ${C.border}`, paddingTop: "10px" }}>
                  <div>correlation · {selected.correlation_id}</div>
                  {selected.received_at && <div>received · {selected.received_at}</div>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ ...innerCardStyle, background: "#fff8e1", borderColor: "#ffe69c" }}>
            <div style={{ fontSize: "12px", fontWeight: "800", color: C.text }}>Sandbox dummy payer</div>
            <div style={{ fontSize: "12px", color: C.textSub }}>
              Unlike a real notice, this needs no prior claim. The notice lands under a new correlation
              id within seconds.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "12px" }}>
              <div>
                <label style={labelStyle}>Provider NHCX code</label>
                <IpdTextField fullWidth value={providerId} onChange={(e) => setProviderId(e.target.value)} />
              </div>
              <div>
                <label style={labelStyle}>Claim number (optional)</label>
                <IpdTextField fullWidth value={claimNumber} onChange={(e) => setClaimNumber(e.target.value)} />
              </div>
            </div>
            <div>
              <IpdButton
                variant="outlined"
                onClick={handleDummyInit}
                disabled={initing}
                loading={initing}
              >
                Trigger payment notice
              </IpdButton>
            </div>
            {initMsg && <div style={{ fontSize: "12px", color: C.text }}>{initMsg}</div>}
          </div>
      </div>
    </div>
  );
}
